import type { QueryClient } from "@tanstack/react-query";
import type { ThreadRuntimeRecoveryStatus } from "@/stores/thread-runtime"
import type { RuntimeConversationActivitySnapshot } from "@/stores/runtime-conversation-activity"

const recordedSessionTitleTimestampBySessionId = new Map<string, number>();

export interface SessionListItem {
  id: string;
  title: string | null;
  status: string;
}

export type ThreadRuntimeState = "idle" | "running" | "error";
export type PullRequestState = "draft" | "open" | "merged" | "closed";
export type PrFlowRuntimeKind =
  | "none"
  | "manual_create_pr"
  | "quick_pr"
  | "update_pr"
  | "merge_pr"
  | "archive_thread"
  | "yolo";
export type PrFlowRuntimeStatus =
  | "idle"
  | "accepted"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "reconciled";
export type PrFlowRuntimeStep =
  | "idle"
  | "validating"
  | "queued"
  | "resuming_sandbox"
  | "awaiting_draft"
  | "awaiting_finalize"
  | "staging"
  | "committing"
  | "pushing"
  | "creating_pr"
  | "linking_existing_pr"
  | "success"
  | "failure";
export type YoloFlowStep = "idle" | "quick_pr" | "merge_pr" | "archiving" | "done" | "error";
export type YoloFlowMergeFailureKind = "conflicting" | "behind" | "blocked" | "draft" | "unknown";

export interface ThreadActivityUpdate {
  sessionId: string;
  state: ThreadRuntimeState;
  eventId: number;
  finishedEventId: number | null;
  finishedAt: number | null;
  finishReason: string | null;
  rawTailId: number | null;
  terminalRawCursor: number | null;
  terminalRawCursorObservedAt: number | null;
}

export interface ThreadStatusSnapshotUpdate {
  sessionId: string;
  activityState?: ThreadRuntimeState | null;
  lastActivityEventId?: number | null;
  lastFinishedEventId?: number | null;
  lastFinishedAt?: number | null;
  lastFinishReason?: string | null;
  rawTailId?: number | null;
  terminalRawCursor?: number | null;
  terminalRawCursorObservedAt?: number | null;
  sandboxStatus?: string | null;
  lifecycleStatus?: string | null;
  persistedStatus?: string | null;
  observedAt?: number | null;
  recoveryStatus?: ThreadRuntimeRecoveryStatus | null;
  recoveryObservedAt?: number | null;
}

export interface PullRequestStatusUpdate {
  sessionId: string;
  prNumber: number;
  prUrl: string;
  prState: PullRequestState;
  headBranch?: string | null;
  baseBranch?: string | null;
  mergeable?: boolean | null;
  mergeableState?: string | null;
  mergeabilityKnown?: boolean;
  timestamp: number;
}

export interface PullRequestFlowUpdate {
  sessionId: string;
  operationKind: PrFlowRuntimeKind;
  operationStatus: PrFlowRuntimeStatus;
  operationStep: PrFlowRuntimeStep;
  timestamp: number | null;
}

export interface YoloFlowUpdate {
  sessionId: string;
  step: YoloFlowStep;
  error: string | null;
  mergeFailureKind: YoloFlowMergeFailureKind | null;
  timestamp: number | null;
}

export interface SessionTitleUpdate {
  sessionId: string;
  title: string;
  timestamp: number;
}

export interface AuthoritativeSessionTitleUpdateResult {
  applied: boolean;
  nextTimestamp: number;
}

export interface ActivityHubSocketMessage {
  type?: unknown;
  changeType?: unknown;
  session?: unknown;
  sessionId?: unknown;
  archivedAt?: unknown;
  repositoryId?: unknown;
  timestamp?: unknown;
  sessions?: unknown;
  activity?: unknown;
  pr?: unknown;
  prFlow?: unknown;
  yoloFlow?: unknown;
  title?: unknown;
}

function hasOwnKey<TKey extends string>(args: {
  value: object;
  key: TKey;
}): boolean {
  return Object.prototype.hasOwnProperty.call(args.value, args.key);
}

function normalizeTrackedSessionId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function readTrackedSessionIdFromUnknown(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  return normalizeTrackedSessionId((value as { sessionId?: unknown }).sessionId);
}

export function filterActivityHubMessageByTrackedSessions(args: {
  message: ActivityHubSocketMessage;
  trackedSessionIds: ReadonlySet<string>;
}): ActivityHubSocketMessage | null {
  const { message, trackedSessionIds } = args;
  const messageType = typeof message.type === "string" ? message.type : null;
  if (!messageType) return message;

  if (
    messageType === "activity.snapshot" ||
    messageType === "activity.pr_snapshot" ||
    messageType === "activity.pr_flow_snapshot" ||
    messageType === "activity.yolo_flow_snapshot" ||
    messageType === "activity.title_snapshot"
    || messageType === "activity.runtime_conversations_snapshot"
  ) {
    if (!Array.isArray(message.sessions)) {
      return message;
    }

    const nextSessions = message.sessions.filter((item) => {
      const sessionId = readTrackedSessionIdFromUnknown(item);
      return sessionId !== null && trackedSessionIds.has(sessionId);
    });

    if (nextSessions.length === 0) {
      return null;
    }

    return {
      ...message,
      sessions: nextSessions,
    };
  }

  const sessionScopedPayload =
    messageType === "activity.delta"
      ? message.activity
      : messageType === "activity.pr_delta"
        ? message.pr
      : messageType === "activity.pr_flow_delta"
        ? message.prFlow
        : messageType === "activity.yolo_flow_delta"
          ? message.yoloFlow
          : messageType === "activity.title_delta"
            ? message.title
            : messageType === "thread_activity"
              ? message
              : null;

  if (sessionScopedPayload === null) {
    return message;
  }

  const sessionId = readTrackedSessionIdFromUnknown(sessionScopedPayload);
  if (sessionId === null) {
    return null;
  }

  return trackedSessionIds.has(sessionId) ? message : null;
}

export function jitterReconnectDelay({ baseDelayMs }: { baseDelayMs: number }): number {
  const factor = 0.5 + Math.random() * 0.5;
  return Math.floor(baseDelayMs * factor);
}

export function buildActivityHubWsUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    return `${apiUrl.replace(/^http/, "ws")}/api/sessions/activity/ws`;
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/api/sessions/activity/ws`;
}

function normalizePositiveInteger({ value }: { value: unknown }): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null;
  }

  return Math.floor(value);
}

function normalizeRuntimeState({ value }: { value: unknown }): ThreadRuntimeState {
  if (value === "running") return "running";
  if (value === "error") return "error";
  return "idle";
}

function normalizePullRequestState({ value }: { value: unknown }): PullRequestState | null {
  if (value === "draft" || value === "open" || value === "merged" || value === "closed") {
    return value;
  }

  return null;
}

function normalizeRecoveryStatus({ value }: { value: unknown }): ThreadRuntimeRecoveryStatus | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  const row = value as Record<string, unknown>
  const level = row.level === "warning" || row.level === "blocked" ? row.level : null
  const label = typeof row.label === "string" ? row.label.trim() : ""
  if (!level || label.length === 0) return null

  return {
    level,
    label,
    detail:
      typeof row.detail === "string" && row.detail.trim().length > 0
        ? row.detail.trim()
        : null,
  }
}

export function normalizeThreadActivityUpdate({ raw }: { raw: unknown }): ThreadActivityUpdate | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;

  const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
  const eventId = normalizePositiveInteger({ value: data.eventId });
  if (!sessionId || eventId === null) return null;

  const state = normalizeRuntimeState({ value: data.state });
  const explicitFinishedEventId = normalizePositiveInteger({ value: data.finishedEventId });

  return {
    sessionId,
    state,
    eventId,
    // Only trust the server-provided finishedEventId. The previous fallback
    // synthesised finishedEventId = eventId for every non-running state, which
    // turned warmup idle-placeholder snapshots into false "just finished"
    // signals — advancing the finish high-water mark and triggering incorrect
    // "unviewed" attention dots and completion notifications.
    finishedEventId: explicitFinishedEventId ?? null,
    finishedAt: normalizePositiveInteger({ value: data.finishedAt }),
    finishReason: typeof data.finishReason === "string" ? data.finishReason : null,
    rawTailId: normalizePositiveInteger({ value: data.rawTailId }),
    terminalRawCursor: normalizePositiveInteger({ value: data.terminalRawCursor }),
    terminalRawCursorObservedAt: normalizePositiveInteger({ value: data.terminalRawCursorObservedAt }),
  };
}

export function normalizeThreadStatusSnapshotUpdate({ raw }: { raw: unknown }): ThreadStatusSnapshotUpdate | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;

  const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
  if (!sessionId) return null;

  const hasActivityState =
    hasOwnKey({ value: data, key: "activityState" }) || hasOwnKey({ value: data, key: "state" });
  const activityState = hasActivityState
    ? normalizeRuntimeState({ value: data.activityState ?? data.state })
    : undefined;
  const hasLastActivityEventId =
    hasOwnKey({ value: data, key: "lastActivityEventId" }) || hasOwnKey({ value: data, key: "eventId" });
  const lastActivityEventId = hasLastActivityEventId
    ? normalizePositiveInteger({ value: data.lastActivityEventId ?? data.eventId })
    : undefined;
  const hasLastFinishedEventId =
    hasOwnKey({ value: data, key: "lastFinishedEventId" }) || hasOwnKey({ value: data, key: "finishedEventId" });
  const lastFinishedEventId = hasLastFinishedEventId
    ? normalizePositiveInteger({ value: data.lastFinishedEventId ?? data.finishedEventId })
    : undefined;
  const hasLastFinishedAt =
    hasOwnKey({ value: data, key: "lastFinishedAt" }) || hasOwnKey({ value: data, key: "finishedAt" });
  const lastFinishedAt = hasLastFinishedAt
    ? normalizePositiveInteger({ value: data.lastFinishedAt ?? data.finishedAt })
    : undefined;
  const hasLastFinishReason =
    hasOwnKey({ value: data, key: "lastFinishReason" }) || hasOwnKey({ value: data, key: "finishReason" });
  const lastFinishReason = hasLastFinishReason
    ? typeof data.lastFinishReason === "string"
      ? data.lastFinishReason
      : typeof data.finishReason === "string"
        ? data.finishReason
        : null
    : undefined;
  const rawTailId = hasOwnKey({ value: data, key: "rawTailId" })
    ? normalizePositiveInteger({ value: data.rawTailId })
    : undefined;
  const terminalRawCursor = hasOwnKey({ value: data, key: "terminalRawCursor" })
    ? normalizePositiveInteger({ value: data.terminalRawCursor })
    : undefined;
  const terminalRawCursorObservedAt = hasOwnKey({ value: data, key: "terminalRawCursorObservedAt" })
    ? normalizePositiveInteger({ value: data.terminalRawCursorObservedAt })
    : undefined;
  const sandboxStatus = hasOwnKey({ value: data, key: "sandboxStatus" })
    ? typeof data.sandboxStatus === "string"
      ? data.sandboxStatus
      : null
    : undefined;
  const lifecycleStatus = hasOwnKey({ value: data, key: "lifecycleStatus" })
    ? typeof data.lifecycleStatus === "string"
      ? data.lifecycleStatus
      : null
    : undefined;
  const hasPersistedStatus =
    hasOwnKey({ value: data, key: "persistedStatus" }) || hasOwnKey({ value: data, key: "status" });
  const persistedStatus = hasPersistedStatus
    ? typeof data.persistedStatus === "string"
      ? data.persistedStatus
      : typeof data.status === "string"
        ? data.status
        : null
    : undefined;
  const hasObservedAt =
    hasOwnKey({ value: data, key: "statusObservedAt" }) || hasOwnKey({ value: data, key: "timestamp" });
  const observedAt = hasObservedAt
    ? normalizePositiveInteger({ value: data.statusObservedAt ?? data.timestamp })
    : undefined;
  const hasRecoveryStatus = hasOwnKey({ value: data, key: "recoveryStatus" })
  const recoveryStatus = hasRecoveryStatus
    ? normalizeRecoveryStatus({ value: data.recoveryStatus })
    : undefined
  const hasRecoveryObservedAt = hasOwnKey({ value: data, key: "recoveryStatusObservedAt" })
  const shouldFallbackRecoveryObservedAtToTimestamp = !(
    hasRecoveryStatus &&
    data.recoveryStatus === null &&
    data.recoveryStatusObservedAt == null &&
    data.statusObservedAt == null
  )
  const recoveryObservedAt = hasRecoveryStatus || hasRecoveryObservedAt
    ? normalizePositiveInteger({
        value: data.recoveryStatusObservedAt ?? data.statusObservedAt ?? (
          shouldFallbackRecoveryObservedAtToTimestamp ? data.timestamp : null
        ),
      })
    : undefined
  const isSyntheticPlaceholder =
    activityState === "idle" &&
    (lastActivityEventId ?? null) === null &&
    (lastFinishedEventId ?? null) === null &&
    (lastFinishedAt ?? null) === null &&
    (lastFinishReason ?? null) === null &&
    (rawTailId ?? null) === null &&
    (terminalRawCursor ?? null) === null &&
    (terminalRawCursorObservedAt ?? null) === null &&
    (sandboxStatus ?? null) === null &&
    (lifecycleStatus ?? null) === null &&
    (persistedStatus ?? null) === null &&
    (recoveryStatus ?? null) === null &&
    (recoveryObservedAt ?? null) === null;

  return {
    sessionId,
    activityState: isSyntheticPlaceholder ? undefined : activityState,
    lastActivityEventId,
    lastFinishedEventId,
    lastFinishedAt,
    lastFinishReason,
    rawTailId,
    terminalRawCursor,
    terminalRawCursorObservedAt,
    sandboxStatus,
    lifecycleStatus,
    persistedStatus,
    observedAt: isSyntheticPlaceholder ? undefined : observedAt,
    recoveryStatus: isSyntheticPlaceholder ? undefined : recoveryStatus,
    recoveryObservedAt: isSyntheticPlaceholder ? undefined : recoveryObservedAt,
  };
}

export function normalizePullRequestStatusUpdate({ raw }: { raw: unknown }): PullRequestStatusUpdate | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;

  const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
  const prNumber = normalizePositiveInteger({ value: data.prNumber });
  const prUrl = typeof data.prUrl === "string" ? data.prUrl.trim() : "";
  const prState = normalizePullRequestState({ value: data.prState });
  const timestamp = normalizePositiveInteger({ value: data.timestamp });

  if (!sessionId || prNumber === null || !prUrl || prState === null || timestamp === null) {
    return null;
  }

  const update: PullRequestStatusUpdate = {
    sessionId,
    prNumber,
    prUrl,
    prState,
    headBranch: typeof data.headBranch === "string" ? data.headBranch.trim() || null : null,
    baseBranch: typeof data.baseBranch === "string" ? data.baseBranch.trim() || null : null,
    timestamp,
  };
  if (
    Object.prototype.hasOwnProperty.call(data, "mergeable") ||
    Object.prototype.hasOwnProperty.call(data, "mergeableState")
  ) {
    update.mergeable = typeof data.mergeable === "boolean" ? data.mergeable : null;
    update.mergeableState = typeof data.mergeableState === "string" ? data.mergeableState.trim() || null : null;
    update.mergeabilityKnown = data.mergeabilityKnown === true;
  }

  return update;
}

export function normalizePullRequestFlowUpdate({ raw }: { raw: unknown }): PullRequestFlowUpdate | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;

  const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
  if (!sessionId) return null;

  const operationKind: PrFlowRuntimeKind =
    data.operationKind === "manual_create_pr" ||
    data.operationKind === "quick_pr" ||
    data.operationKind === "update_pr" ||
    data.operationKind === "merge_pr" ||
    data.operationKind === "archive_thread" ||
    data.operationKind === "yolo"
      ? data.operationKind
      : "none";

  const operationStatus: PrFlowRuntimeStatus =
    data.operationStatus === "accepted" ||
    data.operationStatus === "running" ||
    data.operationStatus === "succeeded" ||
    data.operationStatus === "failed" ||
    data.operationStatus === "cancelled" ||
    data.operationStatus === "reconciled"
      ? data.operationStatus
      : "idle";

  const operationStep: PrFlowRuntimeStep =
    data.operationStep === "validating" ||
    data.operationStep === "queued" ||
    data.operationStep === "resuming_sandbox" ||
    data.operationStep === "awaiting_draft" ||
    data.operationStep === "awaiting_finalize" ||
    data.operationStep === "staging" ||
    data.operationStep === "committing" ||
    data.operationStep === "pushing" ||
    data.operationStep === "creating_pr" ||
    data.operationStep === "linking_existing_pr" ||
    data.operationStep === "success" ||
    data.operationStep === "failure"
      ? data.operationStep
      : "idle";

  return {
    sessionId,
    operationKind,
    operationStatus,
    operationStep,
    timestamp: normalizePositiveInteger({ value: data.timestamp }),
  };
}

function normalizeYoloFlowStep({ value }: { value: unknown }): YoloFlowStep {
  return value === "quick_pr" ||
    value === "merge_pr" ||
    value === "archiving" ||
    value === "done" ||
    value === "error"
    ? value
    : "idle";
}

function normalizeYoloFlowMergeFailureKind({ value }: { value: unknown }): YoloFlowMergeFailureKind | null {
  return value === "conflicting" ||
    value === "behind" ||
    value === "blocked" ||
    value === "draft" ||
    value === "unknown"
    ? value
    : null;
}

export function normalizeYoloFlowUpdate({ raw }: { raw: unknown }): YoloFlowUpdate | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;

  const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
  if (!sessionId) return null;

  const step = normalizeYoloFlowStep({ value: data.step });

  return {
    sessionId,
    step,
    error: step === "error" && typeof data.error === "string" ? data.error.trim() || null : null,
    mergeFailureKind: step === "error"
      ? normalizeYoloFlowMergeFailureKind({ value: data.mergeFailureKind })
      : null,
    timestamp: normalizePositiveInteger({ value: data.timestamp }),
  };
}

export function normalizeSessionTitleUpdate({ raw }: { raw: unknown }): SessionTitleUpdate | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;

  const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
  const title = typeof data.title === "string" ? data.title.trim() : "";
  const timestamp = normalizePositiveInteger({ value: data.timestamp });
  if (!sessionId || !title || timestamp === null) return null;

  return {
    sessionId,
    title,
    timestamp,
  };
}

export function normalizeRuntimeConversationActivitySnapshot({ raw }: { raw: unknown }): RuntimeConversationActivitySnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;
  const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
  const conversationId = typeof data.conversationId === "string" ? data.conversationId.trim() : "";
  if (!sessionId || !conversationId) return null;

  const agentType = data.agentType === "claude_code" || data.agentType === "cursor"
    ? data.agentType
    : "opencode";
  const statusKind =
    data.statusKind === "idle" ||
    data.statusKind === "running" ||
    data.statusKind === "waiting_input" ||
    data.statusKind === "error"
      ? data.statusKind
      : null;
  const activityState = data.activityState === "running" || data.activityState === "error"
    ? data.activityState
    : data.activityState === "idle"
      ? "idle"
      : null;
  const finishReason =
    data.lastFinishReason === "completed" ||
    data.lastFinishReason === "error" ||
    data.lastFinishReason === "aborted" ||
    data.lastFinishReason === "retry_exhausted" ||
    data.lastFinishReason === "waiting_input"
      ? data.lastFinishReason
      : null;

  return {
    sessionId,
    conversationId,
    agentType,
    providerSessionId: typeof data.providerSessionId === "string" ? data.providerSessionId.trim() || null : null,
    title: typeof data.title === "string" ? data.title.trim() || null : null,
    isDefault: data.isDefault === true,
    statusKind,
    activityState,
    activityEventSequence: normalizePositiveInteger({ value: data.activityEventSequence }),
    lastActivityEventId: normalizePositiveInteger({ value: data.lastActivityEventId }),
    lastFinishedEventId: normalizePositiveInteger({ value: data.lastFinishedEventId }),
    lastFinishedAt: normalizePositiveInteger({ value: data.lastFinishedAt }),
    lastFinishReason: finishReason,
    rawTailId: normalizePositiveInteger({ value: data.rawTailId }),
    terminalRawCursor: normalizePositiveInteger({ value: data.terminalRawCursor }),
    terminalRawCursorObservedAt: normalizePositiveInteger({ value: data.terminalRawCursorObservedAt }),
    activityObservedAt: normalizePositiveInteger({ value: data.activityObservedAt }),
    createdAt: normalizePositiveInteger({ value: data.createdAt }) ?? 0,
    updatedAt: normalizePositiveInteger({ value: data.updatedAt }) ?? 0,
    archivedAt: normalizePositiveInteger({ value: data.archivedAt }),
    timestamp: normalizePositiveInteger({ value: data.timestamp }) ?? undefined,
  };
}

export function buildSubscriptionSignature({ sessionIds }: { sessionIds: string[] }): string {
  return Array.from(new Set(sessionIds.map((sessionId) => sessionId.trim()).filter(Boolean)))
    .sort()
    .join(",");
}

export interface CachedMultiplexSession {
  id: string;
  status: string | null;
  lifecycleStatus: string | null;
  archivedAt: string | null;
}

function readScopedActiveMemberRole(args: {
  queryClient: QueryClient;
  normalizedScopeKey: string;
}): string | null {
  const cachedMember = args.queryClient.getQueryData<unknown>([
    "multiplex-thread-activity",
    args.normalizedScopeKey,
    "active-member",
  ]);
  if (!cachedMember || typeof cachedMember !== "object") {
    return null;
  }

  const role = (cachedMember as { role?: unknown }).role;
  return typeof role === "string" && role.trim().length > 0 ? role : null;
}

function shouldReplaceCachedMultiplexSession(args: {
  current: {
    session: CachedMultiplexSession;
    freshness: number;
    sourceKind: "active" | "archived";
  };
  next: {
    session: CachedMultiplexSession;
    freshness: number;
    sourceKind: "active" | "archived";
  };
}): boolean {
  if (args.next.freshness !== args.current.freshness) {
    return args.next.freshness > args.current.freshness;
  }

  if (args.next.sourceKind !== args.current.sourceKind) {
    return args.next.sourceKind === "active";
  }

  const currentArchived = args.current.session.archivedAt !== null;
  const nextArchived = args.next.session.archivedAt !== null;
  if (currentArchived !== nextArchived) {
    return args.next.sourceKind === "archived" ? nextArchived : !nextArchived;
  }

  const currentHasStatus = args.current.session.status !== null;
  const nextHasStatus = args.next.session.status !== null;
  if (currentHasStatus !== nextHasStatus) {
    return nextHasStatus;
  }

  const currentHasLifecycle = args.current.session.lifecycleStatus !== null;
  const nextHasLifecycle = args.next.session.lifecycleStatus !== null;
  if (currentHasLifecycle !== nextHasLifecycle) {
    return nextHasLifecycle;
  }

  return false;
}

export function getCachedMultiplexSessions(args: {
  queryClient: QueryClient;
  scopeKey: string | null;
  activeSessionId: string | null;
}): CachedMultiplexSession[] {
  const normalizedScopeKey =
    typeof args.scopeKey === "string" && args.scopeKey.trim().length > 0
      ? args.scopeKey.trim()
      : null;

  if (normalizedScopeKey === null) {
    return [];
  }

  const scopedActiveMemberRole = readScopedActiveMemberRole({
    queryClient: args.queryClient,
    normalizedScopeKey,
  });
  const includeOrganizationSessions =
    scopedActiveMemberRole === "owner" || scopedActiveMemberRole === "admin";
  const orderedSessionIds: string[] = [];
  const sessionById = new Map<string, {
    session: CachedMultiplexSession;
    freshness: number;
    sourceKind: "active" | "archived";
  }>();

  const upsertSession = (args: {
    value: unknown;
    freshness: number;
    sourceKind: "active" | "archived";
  }) => {
    const { value, freshness, sourceKind } = args;
    if (!value || typeof value !== "object") {
      return;
    }

    const row = value as {
      id?: unknown;
      status?: unknown;
      lifecycleStatus?: unknown;
      archivedAt?: unknown;
    };
    if (typeof row.id !== "string") return;
    const normalized = row.id.trim();
    if (!normalized) return;

    const nextSession = {
      id: normalized,
      status: typeof row.status === "string" ? row.status : null,
      lifecycleStatus: typeof row.lifecycleStatus === "string" ? row.lifecycleStatus : null,
      archivedAt: typeof row.archivedAt === "string" ? row.archivedAt : null,
    } satisfies CachedMultiplexSession;
    const current = sessionById.get(normalized);
    if (!current) {
      orderedSessionIds.push(normalized);
      sessionById.set(normalized, { session: nextSession, freshness, sourceKind });
      return;
    }

    if (shouldReplaceCachedMultiplexSession({
      current,
      next: { session: nextSession, freshness, sourceKind },
    })) {
      sessionById.set(normalized, { session: nextSession, freshness, sourceKind });
    }
  };

  if (typeof args.activeSessionId === "string" && args.activeSessionId.trim().length > 0) {
    upsertSession({ value: { id: args.activeSessionId }, freshness: 0, sourceKind: "active" });
  }

  const cachedQueries = args.queryClient.getQueryCache().findAll({
    queryKey: ["multiplex-thread-activity", normalizedScopeKey, "sessions"],
  });

  for (const cachedQuery of cachedQueries) {
    const queryKey = cachedQuery.queryKey;
    if (
      queryKey[3] === "organization" &&
      !includeOrganizationSessions
    ) {
      continue;
    }

    const data = cachedQuery.state.data;
    if (!Array.isArray(data)) continue;

    const freshness = cachedQuery.state.dataUpdatedAt;
    const sourceKind = queryKey.includes("archived") ? "archived" : "active";
    for (const row of data) {
      upsertSession({ value: row, freshness, sourceKind });
    }
  }

  return orderedSessionIds
    .map((sessionId) => sessionById.get(sessionId)?.session ?? null)
    .filter((session): session is CachedMultiplexSession => session !== null);
}

export function updateSessionListCaches(args: {
  queryClient: QueryClient;
  sessionId: string;
  applyUpdate: (row: Record<string, unknown>) => Record<string, unknown>;
}): void {
  const { queryClient, sessionId, applyUpdate } = args;

  queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
    if (!Array.isArray(old)) return old;

    let changed = false;
    const next = old.map((item) => {
      if (!item || typeof item !== "object") return item;
      const row = item as Record<string, unknown>;
      if (row.id !== sessionId) return item;

      const updatedRow = applyUpdate(row);
      if (updatedRow === row) return item;

      changed = true;
      return updatedRow;
    });

    return changed ? next : old;
  });
}

export function updateSessionDetailCache(args: {
  queryClient: QueryClient;
  sessionId: string;
  applyUpdate: (row: Record<string, unknown>) => Record<string, unknown>;
}): void {
  const { queryClient, sessionId, applyUpdate } = args;

  queryClient.setQueryData(["session", sessionId], (old: unknown) => {
    if (!old || typeof old !== "object") return old;
    return applyUpdate(old as Record<string, unknown>);
  });
}

function readPendingLocalTitleValueFromRow(row: Record<string, unknown>): string | null {
  const value = row.pendingLocalTitleValue;
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readCachedSessionTitleTimestampFromRow(row: Record<string, unknown>): number {
  const activityHubTimestamp = row.activityHubTitleUpdatedAt;
  if (
    typeof activityHubTimestamp === "number" &&
    Number.isFinite(activityHubTimestamp) &&
    activityHubTimestamp > 0
  ) {
    return Math.floor(activityHubTimestamp);
  }

  const titleUpdatedAt = row.titleUpdatedAt;
  if (typeof titleUpdatedAt === "number" && Number.isFinite(titleUpdatedAt) && titleUpdatedAt > 0) {
    return Math.floor(titleUpdatedAt);
  }
  if (typeof titleUpdatedAt === "string") {
    const parsed = Date.parse(titleUpdatedAt);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.floor(parsed);
    }
  }

  return 0;
}

export function applyAuthoritativeSessionTitleUpdate(args: {
  queryClient: QueryClient;
  update: SessionTitleUpdate;
  lastAppliedTimestamp?: number;
}): { applied: boolean; nextTimestamp: number } {
  const normalizedSessionId = typeof args.update.sessionId === "string"
    ? args.update.sessionId.trim()
    : "";
  const results = applyAuthoritativeSessionTitleUpdates({
    queryClient: args.queryClient,
    updates: [args.update],
    lastAppliedTimestampsBySessionId: {
      [normalizedSessionId]: args.lastAppliedTimestamp ?? 0,
    },
  });
  return results[normalizedSessionId] ?? {
    applied: false,
    nextTimestamp: args.lastAppliedTimestamp ?? 0,
  };
}

function buildSessionListTitleMetadataIndex(queryClient: QueryClient): Map<string, {
  cachedTimestamp: number;
  pendingLocalTitleValue: string | null;
}> {
  const index = new Map<string, { cachedTimestamp: number; pendingLocalTitleValue: string | null }>();
  const sessionLists = queryClient.getQueriesData({ queryKey: ["sessions"] });
  for (const [, list] of sessionLists) {
    if (!Array.isArray(list)) continue;

    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const row = item as Record<string, unknown>;
      const sessionId = typeof row.id === "string" ? row.id.trim() : "";
      if (!sessionId) continue;

      const current = index.get(sessionId) ?? {
        cachedTimestamp: 0,
        pendingLocalTitleValue: null,
      };
      const nextTimestamp = readCachedSessionTitleTimestampFromRow(row);
      const nextPendingLocalTitleValue = readPendingLocalTitleValueFromRow(row);
      index.set(sessionId, {
        cachedTimestamp: Math.max(current.cachedTimestamp, nextTimestamp),
        pendingLocalTitleValue: current.pendingLocalTitleValue ?? nextPendingLocalTitleValue,
      });
    }
  }
  return index;
}

function applyAuthoritativeSessionTitleUpdateToRow(args: {
  row: Record<string, unknown>;
  update: SessionTitleUpdate;
  shouldClearPendingLocalTitleValue: boolean;
}): Record<string, unknown> {
  const currentTimestamp = readCachedSessionTitleTimestampFromRow(args.row);
  const currentPendingLocalTitleValue = readPendingLocalTitleValueFromRow(args.row);
  const nextPendingLocalTitleValue = args.shouldClearPendingLocalTitleValue
    ? null
    : currentPendingLocalTitleValue;
  if (args.update.timestamp < currentTimestamp) return args.row;
  if (
    args.row.title === args.update.title &&
    currentTimestamp === args.update.timestamp &&
    currentPendingLocalTitleValue === nextPendingLocalTitleValue
  ) {
    return args.row;
  }
  return {
    ...args.row,
    title: args.update.title,
    activityHubTitleUpdatedAt: args.update.timestamp,
    pendingLocalTitleValue: nextPendingLocalTitleValue,
  };
}

export function applyAuthoritativeSessionTitleUpdates(args: {
  queryClient: QueryClient;
  updates: SessionTitleUpdate[];
  lastAppliedTimestampsBySessionId?: Record<string, number>;
}): Record<string, AuthoritativeSessionTitleUpdateResult> {
  const { queryClient, updates, lastAppliedTimestampsBySessionId } = args;
  if (!Array.isArray(updates) || updates.length === 0) {
    return {};
  }

  const sessionListMetadataIndex = buildSessionListTitleMetadataIndex(queryClient);
  const acceptedUpdates = new Map<string, {
    update: SessionTitleUpdate;
    shouldClearPendingLocalTitleValue: boolean;
  }>();
  const results: Record<string, AuthoritativeSessionTitleUpdateResult> = {};
  const lastKnownTimestampBySessionId = new Map<string, number>();
  const pendingLocalTitleValueBySessionId = new Map<string, string | null>();

  for (const rawUpdate of updates) {
    const sessionId = typeof rawUpdate.sessionId === "string" ? rawUpdate.sessionId.trim() : "";
    if (!sessionId) continue;

    const update = { ...rawUpdate, sessionId };
    const detailRow = queryClient.getQueryData<Record<string, unknown>>(["session", sessionId]);
    const sessionListMetadata = sessionListMetadataIndex.get(sessionId);
    const cachedTimestamp = lastKnownTimestampBySessionId.get(sessionId) ?? Math.max(
      detailRow ? readCachedSessionTitleTimestampFromRow(detailRow) : 0,
      sessionListMetadata?.cachedTimestamp ?? 0,
    );
    const pendingLocalTitleValue = pendingLocalTitleValueBySessionId.has(sessionId)
      ? pendingLocalTitleValueBySessionId.get(sessionId) ?? null
      : readPendingLocalTitleValueFromRow(detailRow ?? {}) ??
        sessionListMetadata?.pendingLocalTitleValue ??
        null;
    const recordedTimestamp = readRecordedSessionTitleTimestamp({ sessionId });
    const lastKnownTimestamp = Math.max(
      lastAppliedTimestampsBySessionId?.[sessionId] ?? 0,
      recordedTimestamp,
      cachedTimestamp,
    );

    lastKnownTimestampBySessionId.set(sessionId, lastKnownTimestamp);
    pendingLocalTitleValueBySessionId.set(sessionId, pendingLocalTitleValue);

    const shouldClearPendingLocalTitleValue =
      pendingLocalTitleValue !== null &&
      (update.title === pendingLocalTitleValue || update.timestamp > lastKnownTimestamp);
    if (
      pendingLocalTitleValue !== null &&
      pendingLocalTitleValue !== update.title &&
      !shouldClearPendingLocalTitleValue
    ) {
      results[sessionId] = { applied: false, nextTimestamp: lastKnownTimestamp };
      continue;
    }

    if (update.timestamp < lastKnownTimestamp) {
      results[sessionId] = { applied: false, nextTimestamp: lastKnownTimestamp };
      continue;
    }

    const nextTimestamp = Math.max(lastKnownTimestamp, update.timestamp);
    recordSessionTitleTimestamp({
      sessionId,
      timestamp: update.timestamp,
    });
    lastKnownTimestampBySessionId.set(sessionId, nextTimestamp);
    pendingLocalTitleValueBySessionId.set(
      sessionId,
      shouldClearPendingLocalTitleValue ? null : pendingLocalTitleValue,
    );
    acceptedUpdates.set(sessionId, { update, shouldClearPendingLocalTitleValue });
    results[sessionId] = { applied: true, nextTimestamp };
  }

  if (acceptedUpdates.size === 0) {
    return results;
  }

  for (const [sessionId, accepted] of acceptedUpdates) {
    queryClient.setQueryData(["session", sessionId], (old: unknown) => {
      if (!old || typeof old !== "object") return old;
      return applyAuthoritativeSessionTitleUpdateToRow({
        row: old as Record<string, unknown>,
        update: accepted.update,
        shouldClearPendingLocalTitleValue: accepted.shouldClearPendingLocalTitleValue,
      });
    });
  }

  queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
    if (!Array.isArray(old)) return old;

    let changed = false;
    const next = old.map((item) => {
      if (!item || typeof item !== "object") return item;
      const row = item as Record<string, unknown>;
      const sessionId = typeof row.id === "string" ? row.id.trim() : "";
      if (!sessionId) return item;

      const accepted = acceptedUpdates.get(sessionId);
      if (!accepted) return item;

      const updatedRow = applyAuthoritativeSessionTitleUpdateToRow({
        row,
        update: accepted.update,
        shouldClearPendingLocalTitleValue: accepted.shouldClearPendingLocalTitleValue,
      });
      if (updatedRow !== row) {
        changed = true;
      }
      return updatedRow;
    });

    return changed ? next : old;
  });

  return results;
}

export function readSessionTitleTimestampFromCaches(args: {
  queryClient: QueryClient;
  sessionId: string;
}): number {
  const { queryClient, sessionId } = args;

  const readTimestamp = (row: unknown): number => {
    if (!row || typeof row !== "object") return 0;
    return readCachedSessionTitleTimestampFromRow(row as Record<string, unknown>);
  };

  const detailTimestamp = readTimestamp(queryClient.getQueryData(["session", sessionId]));
  let maxTimestamp = detailTimestamp;

  const sessionLists = queryClient.getQueriesData({ queryKey: ["sessions"] });
  for (const [, list] of sessionLists) {
    if (!Array.isArray(list)) continue;

    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const row = item as Record<string, unknown>;
      if (row.id !== sessionId) continue;

      const timestamp = readTimestamp(row);
      if (timestamp > maxTimestamp) {
        maxTimestamp = timestamp;
      }
    }
  }

  return maxTimestamp;
}

export function readPendingLocalTitleValueFromCaches(args: {
  queryClient: QueryClient;
  sessionId: string;
}): string | null {
  const { queryClient, sessionId } = args;

  const readPendingValue = (row: unknown): string | null => {
    if (!row || typeof row !== "object") return null;
    return readPendingLocalTitleValueFromRow(row as Record<string, unknown>);
  };

  const detailPendingValue = readPendingValue(queryClient.getQueryData(["session", sessionId]));
  if (detailPendingValue) return detailPendingValue;

  const sessionLists = queryClient.getQueriesData({ queryKey: ["sessions"] });
  for (const [, list] of sessionLists) {
    if (!Array.isArray(list)) continue;

    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const row = item as Record<string, unknown>;
      if (row.id !== sessionId) continue;

      const pendingValue = readPendingValue(row);
      if (pendingValue) return pendingValue;
    }
  }

  return null;
}

export function recordSessionTitleTimestamp(args: {
  sessionId: string;
  timestamp: number | null;
}): void {
  const sessionId = args.sessionId.trim();
  if (!sessionId) return;
  if (typeof args.timestamp !== "number" || !Number.isFinite(args.timestamp) || args.timestamp <= 0) {
    return;
  }

  const normalizedTimestamp = Math.floor(args.timestamp);
  const currentTimestamp = recordedSessionTitleTimestampBySessionId.get(sessionId) ?? 0;
  if (normalizedTimestamp < currentTimestamp) return;
  recordedSessionTitleTimestampBySessionId.set(sessionId, normalizedTimestamp);
}

export function readRecordedSessionTitleTimestamp(args: { sessionId: string }): number {
  const sessionId = args.sessionId.trim();
  if (!sessionId) return 0;
  return recordedSessionTitleTimestampBySessionId.get(sessionId) ?? 0;
}

export function clearRecordedSessionTitleTimestamps(): void {
  recordedSessionTitleTimestampBySessionId.clear();
}
